import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useEffect, useState } from 'react';
import {
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';

interface Question {
  id: number;
  question: string;
  type: 'single_choice' | 'multi_choice';
  options: string[];
  correctAnswers: number[];
  explanation: string;
}

interface Quiz {
  id: number;
  title: string;
  description: string;
  duration: number;
  totalQuestions: number;
  passingScore: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'failed';
  userScore?: number;
  attempts: number;
  maxAttempts: number;
  questions: Question[];
  lastAttempt?: string;
}

export default function QuizScreen({ navigation: navProp, route: routeProp }: { navigation?: any; route?: any } = {}) {
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);

  const [selectedQuiz, setSelectedQuiz] = useState<number | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [userAnswers, setUserAnswers] = useState<{ [questionId: number]: number[] }>({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [showReview, setShowReview] = useState(false);

  const [quizzes, setQuizzes] = useState<Quiz[]>([
    {
      id: 1,
      title: 'EgoCrisis — Pourquoi P2 ?',
      description: 'Testez votre comprehension des licences Entra ID P2 et leur role dans la securite Azure',
      duration: 8,
      totalQuestions: 3,
      passingScore: 70,
      status: 'not_started',
      attempts: 0,
      maxAttempts: 3,
      questions: [
        {
          id: 1,
          question: "Pourquoi la licence P2 est-elle requise ?",
          type: 'single_choice',
          options: [
            "Pour creer des utilisateurs dans Entra ID",
            "Pour les groupes dynamiques et Conditional Access",
            "Pour activer le portail Azure",
            "Pour la gestion des abonnements"
          ],
          correctAnswers: [1],
          explanation: "P1 ne supporte pas les groupes dynamiques ni les politiques d acces conditionnel avancees. P2 est la cle d activation des super-pouvoirs d Entra ID.",
        },
        {
          id: 2,
          question: "Quelle est la source de verite pour l attribution automatique des groupes ?",
          type: 'single_choice',
          options: [
            "Le mot de passe de l utilisateur",
            "Le champ jobTitle",
            "L adresse email",
            "La date de creation du compte"
          ],
          correctAnswers: [1],
          explanation: "Les groupes dynamiques utilisent les metadonnees utilisateur (jobTitle, department) comme criteres de regles. Ex : (user.jobTitle -eq 'Cloud Administrator').",
        },
        {
          id: 3,
          question: "Pourquoi creer un tenant separe plutot qu un resource group isole ?",
          type: 'single_choice',
          options: [
            "Pour reduire les couts",
            "Par obligation Azure",
            "Isolation physique complete",
            "Pour faciliter la gestion des licences"
          ],
          correctAnswers: [2],
          explanation: "Un tenant offre une frontiere physique complete (identites, politiques, logs independants). Un RG isole n est qu une cloison logique dans le meme tenant.",
        },
      ],
    },
    {
      id: 2,
      title: 'Azure Fundamentals',
      description: 'Test your knowledge of Azure cloud concepts and services',
      duration: 15,
      totalQuestions: 3,
      passingScore: 70,
      status: 'not_started',
      attempts: 0,
      maxAttempts: 3,
      questions: [
        {
          id: 4,
          question: 'What is the primary benefit of cloud computing?',
          type: 'single_choice',
          options: ['Lower costs', 'Scalability', 'Security', 'All of the above'],
          correctAnswers: [3],
          explanation: 'Cloud computing provides multiple benefits including lower costs, scalability, and security.',
        },
        {
          id: 5,
          question: 'Which Azure service is used for virtual machines?',
          type: 'single_choice',
          options: ['Azure Functions', 'Azure VMs', 'Azure App Service', 'Azure Storage'],
          correctAnswers: [1],
          explanation: 'Azure VMs is the service specifically designed for running virtual machines in the cloud.',
        },
        {
          id: 6,
          question: 'What are the main types of cloud deployment models? (Select all that apply)',
          type: 'multi_choice',
          options: ['Public Cloud', 'Private Cloud', 'Hybrid Cloud', 'Community Cloud'],
          correctAnswers: [0, 1, 2, 3],
          explanation: 'The four main cloud deployment models are Public, Private, Hybrid, and Community Cloud.',
        },
      ],
    },
  ]);

  const currentQuiz = selectedQuiz ? quizzes.find(q => q.id === selectedQuiz) : null;
  const currentQuestion = currentQuiz?.questions[currentQuestionIndex];

  useEffect(() => {
    if (selectedQuiz && !quizCompleted && !showReview && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { handleCalculateResults(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [selectedQuiz, quizCompleted, showReview, timeLeft]);

  const handleAnswerSelect = (optionIndex: number) => {
    if (!currentQuestion) return;
    if (currentQuestion.type === 'single_choice') {
      setSelectedAnswers([optionIndex]);
    } else {
      setSelectedAnswers(prev =>
        prev.includes(optionIndex) ? prev.filter(i => i !== optionIndex) : [...prev, optionIndex]
      );
    }
  };

  const handleNextQuestion = () => {
    if (!currentQuestion || !currentQuiz) return;
    const updatedAnswers = { ...userAnswers, [currentQuestion.id]: selectedAnswers };
    setUserAnswers(updatedAnswers);
    if (currentQuestionIndex < currentQuiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswers([]);
    } else {
      handleCalculateResults(updatedAnswers);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0 && currentQuestion) {
      setUserAnswers(prev => ({ ...prev, [currentQuestion.id]: selectedAnswers }));
      const prevQuestion = currentQuiz?.questions[currentQuestionIndex - 1];
      setCurrentQuestionIndex(prev => prev - 1);
      if (prevQuestion) setSelectedAnswers(userAnswers[prevQuestion.id] || []);
    }
  };

  const handleCalculateResults = (answers?: { [questionId: number]: number[] }) => {
    if (!currentQuiz) return;
    const finalAnswers = answers ?? userAnswers;
    let correct = 0;
    currentQuiz.questions.forEach(question => {
      const given = finalAnswers[question.id] || [];
      const ok = given.length === question.correctAnswers.length && question.correctAnswers.every(a => given.includes(a));
      if (ok) correct++;
    });
    const score = Math.round((correct / currentQuiz.questions.length) * 100);
    const passed = score >= currentQuiz.passingScore;
    setFinalScore(score);
    setQuizzes(prev => prev.map(q =>
      q.id === currentQuiz.id
        ? { ...q, status: passed ? 'completed' : 'failed', userScore: score, attempts: q.attempts + 1, lastAttempt: new Date().toISOString().split('T')[0] }
        : q
    ));
    setQuizCompleted(true);
  };

  const handleRetakeQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setUserAnswers({});
    setQuizCompleted(false);
    setShowReview(false);
    setFinalScore(0);
    setTimeLeft((currentQuiz?.duration || 15) * 60);
  };

  const handleStartQuiz = (quizId: number) => {
    setSelectedQuiz(quizId);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setUserAnswers({});
    setQuizCompleted(false);
    setShowReview(false);
    setFinalScore(0);
    const quiz = quizzes.find(q => q.id === quizId);
    setTimeLeft((quiz?.duration || 15) * 60);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return colors.success;
      case 'failed': return colors.error;
      case 'in_progress': return colors.primary;
      default: return colors.textTertiary;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Reussi';
      case 'failed': return 'Echoue';
      case 'in_progress': return 'En cours';
      default: return 'Non commence';
    }
  };

  // Results screen with per-question explanations
  if (quizCompleted && currentQuiz) {
    const score = finalScore;
    const passed = score >= currentQuiz.passingScore;

    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <View style={styles.headerContent}>
            <TouchableOpacity style={styles.backButton} onPress={() => { setSelectedQuiz(null); setQuizCompleted(false); }}>
              <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Resultats du Quiz</Text>
            <View style={styles.headerRight} />
          </View>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.contentPadding}>
            <View style={[styles.scoreCard, { backgroundColor: colors.cardBackground }]}>
              <View style={[styles.resultIcon, { backgroundColor: passed ? colors.success : colors.error }]}>
                <Ionicons name={passed ? 'trophy' : 'close-circle'} size={40} color="#FFF" />
              </View>
              <Text style={[styles.resultTitle, { color: colors.textPrimary }]}>
                {passed ? 'Bravo !' : 'Pas encore...'}
              </Text>
              <Text style={[styles.resultSubtitle, { color: colors.textSecondary }]}>
                {passed ? 'Vous avez reussi le quiz !' : 'Continuez a vous entrainer et reessayez.'}
              </Text>
              <View style={styles.scoreRow}>
                <View style={styles.scoreItem}>
                  <Text style={[styles.scoreNumber, { color: passed ? colors.success : colors.error }]}>{score}%</Text>
                  <Text style={[styles.scoreLabel, { color: colors.textSecondary }]}>Votre score</Text>
                </View>
                <View style={[styles.scoreDivider, { backgroundColor: colors.border }]} />
                <View style={styles.scoreItem}>
                  <Text style={[styles.scoreNumber, { color: colors.textPrimary }]}>{currentQuiz.passingScore}%</Text>
                  <Text style={[styles.scoreLabel, { color: colors.textSecondary }]}>Score requis</Text>
                </View>
                <View style={[styles.scoreDivider, { backgroundColor: colors.border }]} />
                <View style={styles.scoreItem}>
                  <Text style={[styles.scoreNumber, { color: colors.textPrimary }]}>
                    {currentQuiz.questions.filter(q => {
                      const given = userAnswers[q.id] || [];
                      return given.length === q.correctAnswers.length && q.correctAnswers.every(a => given.includes(a));
                    }).length}/{currentQuiz.questions.length}
                  </Text>
                  <Text style={[styles.scoreLabel, { color: colors.textSecondary }]}>Correctes</Text>
                </View>
              </View>
            </View>

            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>Revue des questions</Text>
            {currentQuiz.questions.map((question, qIndex) => {
              const given = userAnswers[question.id] || [];
              const isCorrect = given.length === question.correctAnswers.length && question.correctAnswers.every(a => given.includes(a));
              return (
                <View key={question.id} style={[styles.reviewCard, { backgroundColor: colors.cardBackground, borderLeftColor: isCorrect ? colors.success : colors.error }]}>
                  <View style={styles.reviewHeader}>
                    <View style={[styles.reviewBadge, { backgroundColor: isCorrect ? colors.success : colors.error }]}>
                      <Ionicons name={isCorrect ? 'checkmark' : 'close'} size={12} color="#FFF" />
                    </View>
                    <Text style={[styles.reviewQuestionNum, { color: colors.textSecondary }]}>Question {qIndex + 1}</Text>
                  </View>
                  <Text style={[styles.reviewQuestionText, { color: colors.textPrimary }]}>{question.question}</Text>
                  <View style={styles.reviewOptions}>
                    {question.options.map((option, optIndex) => {
                      const isChosen = given.includes(optIndex);
                      const isCorrectAns = question.correctAnswers.includes(optIndex);
                      let bgColor = colors.surfaceSecondary;
                      let borderColor = colors.border;
                      let textColor = colors.textPrimary;
                      if (isChosen && isCorrectAns) { bgColor = isDarkTheme ? '#064E3B' : '#D1FAE5'; borderColor = '#10B981'; textColor = isDarkTheme ? '#6EE7B7' : '#065F46'; }
                      else if (isChosen && !isCorrectAns) { bgColor = isDarkTheme ? '#7F1D1D' : '#FEE2E2'; borderColor = '#EF4444'; textColor = isDarkTheme ? '#FCA5A5' : '#991B1B'; }
                      else if (!isChosen && isCorrectAns) { bgColor = isDarkTheme ? '#032912' : '#F0FDF4'; borderColor = '#10B981'; textColor = isDarkTheme ? '#6EE7B7' : '#065F46'; }
                      return (
                        <View key={optIndex} style={[styles.reviewOption, { backgroundColor: bgColor, borderColor }]}>
                          <Text style={[styles.reviewOptionText, { color: textColor }]}>{option}</Text>
                          {isChosen && !isCorrectAns && <Ionicons name="close-circle" size={16} color="#EF4444" />}
                          {isCorrectAns && <Ionicons name="checkmark-circle" size={16} color="#10B981" />}
                        </View>
                      );
                    })}
                  </View>
                  <View style={[styles.explanationBox, { backgroundColor: isDarkTheme ? '#1E293B' : '#F8FAFC', borderColor: colors.border }]}>
                    <Text style={[styles.explanationLbl, { color: colors.primary }]}>Explication</Text>
                    <Text style={[styles.explanationText, { color: colors.textSecondary }]}>{question.explanation}</Text>
                  </View>
                </View>
              );
            })}

            <View style={styles.actionButtons}>
              <TouchableOpacity style={[styles.primaryButton, { backgroundColor: colors.primary }]} onPress={() => { setSelectedQuiz(null); setQuizCompleted(false); }}>
                <Text style={[styles.primaryButtonText, { color: colors.badgeText }]}>Retour aux quiz</Text>
              </TouchableOpacity>
              {!passed && (currentQuiz.attempts ?? 0) < currentQuiz.maxAttempts && (
                <TouchableOpacity style={[styles.secondaryButton, { borderColor: colors.border }]} onPress={handleRetakeQuiz}>
                  <Ionicons name="refresh" size={16} color={colors.primary} />
                  <Text style={[styles.secondaryButtonText, { color: colors.primary }]}>Recommencer</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  // Review mode
  if (showReview && selectedQuiz && currentQuiz) {
    const reviewQuestion = currentQuiz.questions[currentQuestionIndex];
    if (!reviewQuestion) {
      return (
        <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
          <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
            <View style={styles.headerContent}>
              <TouchableOpacity style={styles.backButton} onPress={() => setShowReview(false)}>
                <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
              </TouchableOpacity>
              <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Revue terminee</Text>
              <View style={styles.headerRight} />
            </View>
          </View>
          <View style={styles.centerContent}>
            <Text style={[styles.centerTitle, { color: colors.textPrimary }]}>Revue Complete</Text>
            <TouchableOpacity style={[styles.primaryButton, { backgroundColor: colors.primary }]} onPress={() => setShowReview(false)}>
              <Text style={[styles.primaryButtonText, { color: colors.badgeText }]}>Retour</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      );
    }
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <View style={styles.headerContent}>
            <TouchableOpacity style={styles.backButton} onPress={() => setShowReview(false)}>
              <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
            </TouchableOpacity>
            <View style={styles.headerInfo}>
              <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{currentQuiz.title} — Revue</Text>
              <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>Question {currentQuestionIndex + 1} / {currentQuiz.questions.length}</Text>
            </View>
            <View style={[styles.reviewBadgeHeader, { backgroundColor: colors.surfaceSecondary }]}>
              <Text style={[styles.reviewBadgeHeaderText, { color: colors.textSecondary }]}>Revue</Text>
            </View>
          </View>
          <View style={[styles.progressBar, { backgroundColor: colors.surfaceSecondary }]}>
            <View style={[styles.progressFill, { backgroundColor: colors.primary, width: `${((currentQuestionIndex + 1) / currentQuiz.questions.length) * 100}%` }]} />
          </View>
        </View>
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.contentPadding}>
            <View style={[styles.questionCard, { backgroundColor: colors.cardBackground }]}>
              <Text style={[styles.questionText, { color: colors.textPrimary }]}>{reviewQuestion.question}</Text>
              <View style={styles.optionsContainer}>
                {reviewQuestion.options.map((option, index) => {
                  const isCorrect = reviewQuestion.correctAnswers.includes(index);
                  return (
                    <View key={index} style={[styles.optionItem, { backgroundColor: isCorrect ? colors.success : colors.surfaceSecondary, borderColor: isCorrect ? colors.success : colors.border }]}>
                      <Text style={[styles.optionText, { color: isCorrect ? colors.badgeText : colors.textPrimary }]}>{option}</Text>
                      {isCorrect && <Ionicons name="checkmark-circle" size={20} color={colors.badgeText} />}
                    </View>
                  );
                })}
              </View>
            </View>
            <View style={[styles.explanationCard, { backgroundColor: colors.surfaceSecondary }]}>
              <Text style={[styles.explanationLbl, { color: colors.textPrimary }]}>Explication</Text>
              <Text style={[styles.explanationText, { color: colors.textSecondary }]}>{reviewQuestion.explanation}</Text>
            </View>
          </View>
        </ScrollView>
        <View style={[styles.navigationBar, { backgroundColor: colors.surface, borderTopColor: colors.border }]}>
          <View style={styles.pillRow}>
            <TouchableOpacity style={[styles.closePill, { borderColor: colors.border }, currentQuestionIndex === 0 && styles.navButtonDisabled]} onPress={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))} disabled={currentQuestionIndex === 0}>
              <Text style={[styles.closePillText, { color: currentQuestionIndex === 0 ? colors.textTertiary : colors.textSecondary }]}>{'←'} Precedent</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.nextPill, { backgroundColor: colors.primary }]} onPress={() => setCurrentQuestionIndex(prev => prev + 1)}>
              <Text style={styles.nextPillText}>
                {currentQuestionIndex === currentQuiz.questions.length - 1 ? 'Terminer' : 'Suivant →'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // Active quiz
  if (selectedQuiz && currentQuestion && !quizCompleted && !showReview) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <View style={styles.headerContent}>
            <TouchableOpacity style={styles.backButton} onPress={() => setSelectedQuiz(null)}>
              <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
            </TouchableOpacity>
            <View style={styles.headerInfo}>
              <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{currentQuiz?.title}</Text>
              <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>Question {currentQuestionIndex + 1} / {currentQuiz?.questions.length}</Text>
            </View>
            <View style={styles.timerContainer}>
              <Ionicons name="time" size={16} color={colors.textTertiary} />
              <Text style={[styles.timerText, { color: colors.textTertiary }]}>{formatTime(timeLeft)}</Text>
            </View>
          </View>
          <View style={[styles.progressBar, { backgroundColor: colors.surfaceSecondary }]}>
            <View style={[styles.progressFill, { backgroundColor: colors.primary, width: `${((currentQuestionIndex + 1) / (currentQuiz?.questions.length ?? 1)) * 100}%` }]} />
          </View>
        </View>
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.contentPadding}>
            <View style={[styles.questionCard, { backgroundColor: colors.cardBackground }]}>
              <Text style={[styles.questionText, { color: colors.textPrimary }]}>{currentQuestion.question}</Text>
              <View style={styles.optionsContainer}>
                {currentQuestion.options.map((option, index) => {
                  const isSelected = selectedAnswers.includes(index);
                  return (
                    <TouchableOpacity key={index} style={[styles.optionItem, { backgroundColor: isSelected ? colors.primary : colors.cardBackground, borderColor: isSelected ? colors.primary : colors.border }]} onPress={() => handleAnswerSelect(index)}>
                      <Text style={[styles.optionText, { color: isSelected ? colors.badgeText : colors.textPrimary }]}>{option}</Text>
                      {isSelected && <Ionicons name="checkmark-circle" size={20} color={colors.badgeText} />}
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          </View>
        </ScrollView>
        <View style={[styles.navigationBar, { backgroundColor: colors.surface, borderTopColor: colors.border }]}>
          <View style={styles.pillRow}>
            <TouchableOpacity style={[styles.closePill, { borderColor: colors.border }, currentQuestionIndex === 0 && styles.navButtonDisabled]} onPress={handlePreviousQuestion} disabled={currentQuestionIndex === 0}>
              <Text style={[styles.closePillText, { color: currentQuestionIndex === 0 ? colors.textTertiary : colors.textSecondary }]}>{'←'} Precedent</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.nextPill, { backgroundColor: selectedAnswers.length === 0 ? colors.surfaceSecondary : colors.primary }]} onPress={handleNextQuestion} disabled={selectedAnswers.length === 0}>
              <Text style={[styles.nextPillText, { color: selectedAnswers.length === 0 ? colors.textTertiary : colors.badgeText }]}>
                {currentQuestionIndex === (currentQuiz?.questions.length ?? 1) - 1 ? 'Terminer' : 'Suivant →'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // Quiz list
  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
          </TouchableOpacity>
          <View style={styles.headerInfo}>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Quiz</Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
              {route.params?.chapterId ? `Chapitre ${route.params.chapterId}` : 'Testez vos connaissances'}
            </Text>
          </View>
          <Ionicons name="bulb" size={24} color={colors.textTertiary} />
        </View>
      </View>
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.contentPadding}>
          {quizzes.map((quiz) => (
            <View key={quiz.id} style={[styles.quizCard, { backgroundColor: colors.cardBackground }]}>
              <View style={styles.quizHeader}>
                <View style={styles.quizTitleRow}>
                  <Text style={[styles.quizTitle, { color: colors.textPrimary }]}>{quiz.title}</Text>
                  <View style={[styles.statusBadge, { backgroundColor: getStatusColor(quiz.status) }]}>
                    <Text style={[styles.statusBadgeText, { color: colors.badgeText }]}>{getStatusLabel(quiz.status)}</Text>
                  </View>
                </View>
                <Text style={[styles.quizDescription, { color: colors.textSecondary }]}>{quiz.description}</Text>
              </View>
              <View style={styles.quizStats}>
                <View style={styles.statItem}>
                  <Ionicons name="time" size={14} color={colors.textTertiary} />
                  <Text style={[styles.statText, { color: colors.textSecondary }]}>{quiz.duration} min</Text>
                </View>
                <View style={styles.statItem}>
                  <Ionicons name="bulb" size={14} color={colors.textTertiary} />
                  <Text style={[styles.statText, { color: colors.textSecondary }]}>{quiz.totalQuestions} questions</Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={[styles.statText, { color: colors.textSecondary }]}>Seuil : {quiz.passingScore}%</Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={[styles.statText, { color: colors.textSecondary }]}>Tentatives : {quiz.attempts}/{quiz.maxAttempts}</Text>
                </View>
              </View>
              {(quiz.status === 'completed' || quiz.status === 'failed') && (
                <View style={styles.completedInfo}>
                  <Ionicons name="checkmark-circle" size={16} color={quiz.status === 'completed' ? colors.success : colors.error} />
                  <Text style={[styles.completedText, { color: colors.textSecondary }]}>Score : {quiz.userScore}%</Text>
                </View>
              )}
              <View style={styles.quizActions}>
                {quiz.status === 'not_started' || quiz.status === 'in_progress' ? (
                  <TouchableOpacity style={[styles.primaryButton, { backgroundColor: colors.primary }]} onPress={() => handleStartQuiz(quiz.id)}>
                    <Text style={[styles.primaryButtonText, { color: colors.badgeText }]}>
                      {quiz.status === 'in_progress' ? 'Continuer' : 'Commencer'}
                    </Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.completedActions}>
                    <TouchableOpacity style={[styles.secondaryButton, { borderColor: colors.border }]} onPress={() => { setSelectedQuiz(quiz.id); setShowReview(true); setCurrentQuestionIndex(0); }}>
                      <Ionicons name="eye" size={14} color={colors.primary} />
                      <Text style={[styles.secondaryButtonText, { color: colors.primary }]}>Revoir</Text>
                    </TouchableOpacity>
                    {quiz.attempts < quiz.maxAttempts && (
                      <TouchableOpacity style={[styles.secondaryButton, { borderColor: colors.border }]} onPress={() => handleStartQuiz(quiz.id)}>
                        <Ionicons name="refresh" size={14} color={colors.primary} />
                        <Text style={[styles.secondaryButtonText, { color: colors.primary }]}>Recommencer</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { borderBottomWidth: 1, paddingHorizontal: 20, paddingVertical: 16 },
  headerContent: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  backButton: { padding: 8, marginLeft: -8, marginRight: 12 },
  headerInfo: { flex: 1 },
  headerTitle: { fontSize: 18, fontWeight: '600', marginBottom: 2 },
  headerSubtitle: { fontSize: 12 },
  headerRight: { width: 40 },
  timerContainer: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  timerText: { fontSize: 14, fontWeight: '500' },
  reviewBadgeHeader: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12 },
  reviewBadgeHeaderText: { fontSize: 12, fontWeight: '500' },
  progressBar: { height: 4, borderRadius: 2, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 2 },
  content: { flex: 1 },
  contentPadding: { padding: 16 },
  centerContent: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  centerTitle: { fontSize: 20, fontWeight: '600', marginBottom: 16 },
  scoreCard: { borderRadius: 16, padding: 20, marginBottom: 20, alignItems: 'center' },
  resultIcon: { width: 72, height: 72, borderRadius: 36, alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  resultTitle: { fontSize: 22, fontWeight: '700', marginBottom: 4 },
  resultSubtitle: { fontSize: 14, textAlign: 'center', marginBottom: 20 },
  scoreRow: { flexDirection: 'row', alignItems: 'center', gap: 8, width: '100%' },
  scoreItem: { flex: 1, alignItems: 'center' },
  scoreNumber: { fontSize: 22, fontWeight: '700', marginBottom: 2 },
  scoreLabel: { fontSize: 11 },
  scoreDivider: { width: 1, height: 40 },
  sectionTitle: { fontSize: 17, fontWeight: '700', marginBottom: 12 },
  reviewCard: { borderRadius: 12, padding: 16, marginBottom: 16, borderLeftWidth: 4 },
  reviewHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  reviewBadge: { width: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  reviewQuestionNum: { fontSize: 11, fontWeight: '600' },
  reviewQuestionText: { fontSize: 15, fontWeight: '600', marginBottom: 12, lineHeight: 22 },
  reviewOptions: { gap: 8, marginBottom: 12 },
  reviewOption: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  reviewOptionText: { fontSize: 13, flex: 1, marginRight: 8 },
  explanationBox: { borderWidth: 1, borderRadius: 10, padding: 12 },
  explanationLbl: { fontSize: 12, fontWeight: '700', marginBottom: 4 },
  explanationText: { fontSize: 13, lineHeight: 19 },
  questionCard: { borderRadius: 12, padding: 16, marginBottom: 16 },
  questionText: { fontSize: 17, fontWeight: '500', marginBottom: 16, lineHeight: 24 },
  optionsContainer: { gap: 10 },
  optionItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderRadius: 8, borderWidth: 1, padding: 14 },
  optionText: { fontSize: 14, flex: 1, marginRight: 8 },
  explanationCard: { borderRadius: 12, padding: 16 },
  navigationBar: { borderTopWidth: 1, paddingHorizontal: 16, paddingVertical: 12 },
  navigationButtons: { flexDirection: 'row', gap: 12 },
  pillRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8 },
  navButton: { flex: 1, paddingVertical: 12, paddingHorizontal: 24, borderRadius: 8, alignItems: 'center', borderWidth: 1 },
  navButtonDisabled: { opacity: 0.5 },
  navButtonText: { fontSize: 15, fontWeight: '600' },
  closePill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1, backgroundColor: 'transparent' },
  closePillText: { fontSize: 14, fontWeight: '600' },
  nextPill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 },
  nextPillText: { color: '#FFF', fontSize: 14, fontWeight: '600' },
  quizCard: { borderRadius: 12, padding: 16, marginBottom: 16 },
  quizHeader: { marginBottom: 12 },
  quizTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  quizTitle: { fontSize: 16, fontWeight: '600', flex: 1 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12 },
  statusBadgeText: { fontSize: 11, fontWeight: '600' },
  quizDescription: { fontSize: 13, lineHeight: 19 },
  quizStats: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 12 },
  statItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  statText: { fontSize: 12 },
  completedInfo: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  completedText: { fontSize: 12 },
  quizActions: { gap: 8 },
  completedActions: { flexDirection: 'row', gap: 8 },
  actionButtons: { gap: 12, marginTop: 8, marginBottom: 24 },
  primaryButton: { paddingVertical: 12, paddingHorizontal: 24, borderRadius: 8, alignItems: 'center' },
  primaryButtonText: { fontSize: 15, fontWeight: '600' },
  secondaryButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 11, paddingHorizontal: 16, borderRadius: 8, borderWidth: 1, gap: 6 },
  secondaryButtonText: { fontSize: 14, fontWeight: '500' },
});
